import type { TiptapDoc } from '@odo/shared-ui';
import type { CommunicationChannel } from '../../../../services/communications';

export interface SeedSection {
    id: string;
    heading: string;
}

// Per-channel starting sections. text and dashboard share a generic set.
const SEED_SECTIONS_BY_CHANNEL: Record<CommunicationChannel, readonly SeedSection[]> = {
    call: [
        { id: 'issue', heading: 'Issue / concern' },
        { id: 'actions', heading: 'Actions taken' },
        { id: 'next-steps', heading: 'Next steps' }
    ],
    email: [
        { id: 'asked', heading: 'What they asked' },
        { id: 'replied', heading: 'What we replied' },
        { id: 'next-steps', heading: 'Next steps' }
    ],
    note: [
        { id: 'context', heading: 'Context' },
        { id: 'decision', heading: 'Decision' }
    ],
    text: [
        { id: 'asked', heading: 'What they asked' },
        { id: 'replied', heading: 'Our reply' }
    ],
    dashboard: [
        { id: 'asked', heading: 'What they asked' },
        { id: 'replied', heading: 'Our reply' }
    ]
};

export function seedSectionsFor(channel: CommunicationChannel): readonly SeedSection[] {
    return SEED_SECTIONS_BY_CHANNEL[channel];
}

interface TiptapTextNode {
    type?: string;
    text?: string;
}

interface TiptapNode {
    type?: string;
    attrs?: { level?: number };
    content?: TiptapTextNode[];
}

function headingNode(text: string): TiptapNode {
    return { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text }] };
}

const EMPTY_PARAGRAPH: TiptapNode = { type: 'paragraph' };

function hasHeading(doc: TiptapDoc, text: string): boolean {
    return (doc.content as TiptapNode[]).some(
        (node) => node.type === 'heading' && (node.content ?? []).some((c) => c.text === text)
    );
}

/** Appends a section heading (level 2) + an empty paragraph, unless that heading is already present. */
export function insertSeedSection(doc: TiptapDoc, heading: string): TiptapDoc {
    if (hasHeading(doc, heading)) return doc;
    return {
        ...doc,
        content: [...doc.content, headingNode(heading), EMPTY_PARAGRAPH]
    };
}

/** Inserts every section the active channel offers that isn't already present, in its canonical order. */
export function insertAllSeedSections(doc: TiptapDoc, channel: CommunicationChannel): TiptapDoc {
    return seedSectionsFor(channel).reduce(
        (acc, section) => insertSeedSection(acc, section.heading),
        doc
    );
}
